/**
 * The embryo-development step's grades, one row per embryo.
 *
 * A clinic grades each embryo, so the step asks for each one: "Embryo 1",
 * "Embryo 2" and on, each with its grade picked from compact chips, and "Add
 * embryo" for the next. One row is open at a time, so the list stays short.
 * The best grade is only ever worked out from them, as a line under the list.
 *
 * The API keeps a grade for each embryo and no day of development, so no day
 * is asked for. Taking the grade off an embryo, by tapping its chip again, is
 * what takes it off the list when the step is saved.
 */

import { useState } from "react";
import { View } from "react-native";
import { ChevronDown, ChevronUp, Plus, Sparkles } from "lucide-react-native";
import {
  MetaText,
  MoonFieldInput,
  MoonFilterChip,
  MoonFormField,
  PrimaryTitleText,
  Row,
  TextActionButton,
  colors,
} from "@louise/moon-ui";
import {
  bestEmbryoGrade,
  type EmbryoGradeEntry,
} from "@src/packages/patients/embryoGrades";
import { useTranslations } from "@src/packages/i18n";

const EMPTY_EMBRYO: EmbryoGradeEntry = { id: null, grade: null };

export function EmbryoGradesField({
  label,
  grades,
  value,
  onChange,
}: {
  label: string;
  /** The step's grades, best first. */
  grades: readonly string[];
  value: readonly EmbryoGradeEntry[];
  onChange: (next: EmbryoGradeEntry[]) => void;
}) {
  const t = useTranslations();
  // A step with no embryos yet starts with the first one, open and waiting
  // for its grade. A saved list starts closed, showing each grade.
  const embryos = value.length > 0 ? value : [EMPTY_EMBRYO];
  const [openIndex, setOpenIndex] = useState<number | null>(
    value.length > 0 ? null : 0,
  );
  const best = bestEmbryoGrade(embryos, grades);

  const setGrade = (index: number, grade: string | null) =>
    onChange(
      embryos.map((embryo, position) =>
        position === index ? { ...embryo, grade } : embryo,
      ),
    );

  return (
    <MoonFormField label={label} labelIcon={Sparkles}>
      <View style={{ gap: 8 }}>
        {embryos.map((embryo, index) => {
          const open = openIndex === index;
          const Chevron = open ? ChevronUp : ChevronDown;
          const title = t("care.treatment_hub.embryo_grades.embryo_number", {
            params: { number: String(index + 1) },
          });
          return (
            <MoonFieldInput key={embryo.id ?? `new-${index}`} paddingY={10}>
              <Row
                surface="plain"
                density="dense"
                paddingHorizontal={0}
                paddingVertical={0}
                title={title}
                trailing={
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {embryo.grade ? (
                      <PrimaryTitleText>{embryo.grade}</PrimaryTitleText>
                    ) : (
                      <MetaText style={{ marginTop: 0 }}>
                        {t("care.treatment_hub.embryo_grades.add_grade")}
                      </MetaText>
                    )}
                    <Chevron
                      size={15}
                      color={colors.textLight}
                      strokeWidth={1.8}
                    />
                  </View>
                }
                chevron={false}
                action="embryo_grade_row"
                accessibilityLabel={title}
                accessibilityState={{ expanded: open }}
                testID={`embryo-grade-row-${index + 1}`}
                onPress={() => setOpenIndex(open ? null : index)}
              />
              {open ? (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 12,
                  }}
                >
                  {grades.map((grade) => (
                    <MoonFilterChip
                      key={grade}
                      label={grade}
                      size="sm"
                      showCheck={false}
                      selected={embryo.grade === grade}
                      // Tapping her grade again takes it back.
                      onPress={() =>
                        setGrade(index, embryo.grade === grade ? null : grade)
                      }
                      // The grade itself, as the chip's press id always was.
                      action={grade}
                    />
                  ))}
                </View>
              ) : null}
            </MoonFieldInput>
          );
        })}
        <TextActionButton
          label={t("care.treatment_hub.embryo_grades.add_embryo")}
          action="add_embryo_grade"
          size="sm"
          icon={Plus}
          onPress={() => {
            onChange([...embryos, EMPTY_EMBRYO]);
            setOpenIndex(embryos.length);
          }}
          testID="embryo-grade-add"
          style={{ alignSelf: "flex-start", marginLeft: 2 }}
        />
        {best ? (
          <MetaText style={{ marginTop: 0, marginLeft: 2 }}>
            {t("care.treatment_hub.embryo_grades.best_grade_of_embryo", {
              params: {
                grade: best.grade,
                number: String(best.embryoNumber),
              },
            })}
          </MetaText>
        ) : null}
      </View>
    </MoonFormField>
  );
}
